import { useCallback, useMemo, useRef, useState } from 'react';
import { AlertCircle, Download } from 'lucide-react';
import {
  ColumnDef,
  PaginationState,
  RowSelectionState,
  Row,
} from '@tanstack/react-table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import ZipExportNotice from '@/components/documents/ZipExportNotice';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTable, DataTableHandle } from '@/components/ui/DataTable';
import {
  ColumnManagerPopover,
  ManagedColumn,
} from '@/components/ui/ColumnManagerPopover';
import { DateRangeFilter } from '@/components/ui/DateRangeFilter';
import { ExportCsvButton } from '@/components/ui/ExportCsvButton';
import { TableStateGate } from '@/components/ui/TableStateGate';
import {
  DateRangeFilterValue,
  isWithinDateRange,
} from '@/components/ui/tableFilterFns';
import { useColumnPreferences } from '@/hooks/useColumnPreferences';
import { formatTimestamp, formatTimeOfDay } from '@/utils/dates';
import type { ReleasedDocument } from '@/hooks/releases/useReleasedDocuments';

interface ReleasedDocumentsTableProps {
  documents: ReleasedDocument[];
  isLoading: boolean;
  error: unknown;
  onDownload: (documentIds: string[]) => void;
  isDownloading?: boolean;
  downloadError?: string | null;
  /**
   * OV-1405. Set when the last download was large enough to be prepared in the
   * background; the table shows its progress in place of the download error.
   */
  zipExportId?: string | null;
  /**
   * Study table only. The site and submission tables are already scoped to a
   * single site / single submission, so the column there would repeat the page
   * header. Off by default — adding a column is the deliberate act.
   */
  showSite?: boolean;
  /**
   * Signed-in user, so stored column preferences don't leak across accounts on
   * a shared machine. Passed in rather than read from UserProvider here: this
   * component is rendered by three pages and exercised by tests that have no
   * provider, and column layout is not worth making it context-bound.
   * Undefined simply means "don't persist" — the table falls back to defaults.
   */
  userId?: string;
}

const TABLE_ID = 'final-documents';

const COLUMN_LABELS: Record<string, string> = {
  filename: 'Document',
  site: 'Site',
  associated_submission: 'Associated Submission',
  released_at: 'Released',
};

/**
 * Row de-emphasis. This is what replaced the Status badge (see the class
 * comment): a superseded or withdrawn row is greyed rather than labelled.
 * Applied per cell because the shared DataTable owns the <tr>.
 */
const rowTone = (status: ReleasedDocument['status']): string | undefined => {
  if (status === 'withdrawn') return 'text-gray-400';
  if (status === 'superseded') return 'text-gray-500';
  return undefined;
};

/**
 * Final Documents, customer-facing. Shared by the study, site and submission
 * tabs.
 *
 * One row per released document, showing the RELEASE date — never the
 * document's modified date, which would leak internal activity timing and
 * mislead the customer about when they actually received something.
 *
 * A document released, revised and released again appears twice.
 *
 * The Status column was removed on 14 September (customer items R-10/R-13):
 * Current/Superseded implied version control Univo cannot offer. A document
 * released, revised and released again still appears twice, and the earlier
 * row is still de-emphasised and still downloadable — a site may have been
 * consenting patients under the prior consent form — but nothing names it.
 *
 * Withdrawal is the exception, and the reason the column could not simply go:
 * it was the only place a withdrawn document's trace showed. The trace moves
 * under the filename as "Withdrawn {date}", and the row stays unselectable.
 *
 * The words "version", "release", "packet", "superseded" and "current" are
 * deliberately absent from all copy here, the accessibility tree included.
 *
 * OV-1365 moved the table onto the shared `DataTable`, so it now carries the
 * same filtering / pagination / column management / CSV export as every other
 * list view. The "Provided by" column went at the same time (it read "Univo"
 * for Univo-released documents); the blueprint still sends `uploaded_by`.
 */
const ReleasedDocumentsTable = ({
  documents,
  isLoading,
  error,
  onDownload,
  isDownloading = false,
  downloadError = null,
  zipExportId = null,
  showSite = false,
  userId,
}: ReleasedDocumentsTableProps) => {
  const dataTableRef = useRef<DataTableHandle>(null);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [tableRowCount, setTableRowCount] = useState(0);
  const [releasedRange, setReleasedRange] = useState<DateRangeFilterValue>({
    from: null,
    to: null,
  });
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  // Which columns this surface actually has. The study tab adds Site; the
  // others are already scoped to one site, so it would repeat the header.
  const manageableColumns = useMemo(
    () =>
      [
        {
          id: 'filename',
          defaultVisible: true,
          defaultPinned: 'left' as const,
        },
        ...(showSite ? [{ id: 'site', defaultVisible: true }] : []),
        { id: 'associated_submission', defaultVisible: true },
        { id: 'released_at', defaultVisible: true },
      ].map(column => ({ ...column })),
    [showSite]
  );

  const defaultOrder = useMemo(
    () => manageableColumns.map(column => column.id),
    [manageableColumns]
  );

  const {
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    columnPinning,
    setColumnPinning,
    resetToDefaults,
  } = useColumnPreferences({
    // Study and site/submission tables have different column sets, so they
    // get different storage keys — otherwise a stored order containing
    // "site" would be replayed onto a table that has no such column.
    tableId: showSite ? `${TABLE_ID}-study` : TABLE_ID,
    userId,
    columns: manageableColumns,
    defaultOrder,
  });

  // OXX-1076: withdrawn rows are listed but NOT selectable. The server
  // excludes them from the ZIP and fails the whole request rather than
  // returning a short one, so including a withdrawn row in a selection kills
  // the download for every other document in it. Selecting one has to be
  // impossible here rather than failing later with nothing to explain it.
  const isSelectable = useCallback(
    (row: Row<ReleasedDocument>) => row.original.status !== 'withdrawn',
    []
  );

  // Selectable rows, narrowed by the active Released filter. It has to track
  // the filter, not the raw `documents` prop: a row the user has filtered out
  // is one they can no longer see, so it must not stay in the selection, get
  // counted by the toolbar, or end up in the ZIP. Withdrawn rows are excluded
  // for the separate reason above.
  const selectableIds = useMemo(
    () =>
      documents
        .filter(
          doc =>
            doc.status !== 'withdrawn' &&
            // The same predicate the column filter runs, so the two can never
            // disagree about which rows the range covers.
            isWithinDateRange(doc.released_at, releasedRange)
        )
        .map(doc => doc.id),
    [documents, releasedRange]
  );

  // Selection is keyed on the row id, never the filename: a superseded row and
  // its current replacement share a filename by definition. DataTable's
  // `getRowId` makes these keys the document ids, so they can go straight to
  // the download call.
  const selectedIds = useMemo(
    () => selectableIds.filter(id => rowSelection[id]),
    [selectableIds, rowSelection]
  );

  // "filename|date" keys that more than one row shares, so the per-row label
  // can ask in O(1) whether the release date alone identifies it. Built once
  // per document list rather than scanning every other row inside the render
  // map, which was O(n²) Date formatting on each render.
  const sameDayFilenames = useMemo(() => {
    const seen = new Map<string, number>();
    documents.forEach(doc => {
      const key = `${doc.filename}|${formatTimestamp(doc.released_at)}`;
      seen.set(key, (seen.get(key) ?? 0) + 1);
    });
    return new Set(
      [...seen.entries()].filter(([, count]) => count > 1).map(([key]) => key)
    );
  }, [documents]);

  // The label has to distinguish two rows that share a filename by definition.
  // It used to say "(superseded)"; the release date does the same job without
  // the word the customer asked us to drop.
  //
  // But formatTimestamp is DAY-granular, so two versions released on the same
  // day produce identical labels and a screen-reader user gets two
  // indistinguishable checkboxes — the very ambiguity the "(superseded)"
  // suffix existed to prevent.
  //
  // The disambiguator is the TIME of release, never the status word: the class
  // comment above binds the accessibility tree too, so appending doc.status
  // here would smuggle "superseded" / "current" back into exactly the place
  // R-10/R-13 removed them from. Time is added only when the date alone cannot
  // separate the rows, so the common case keeps the agreed wording.
  const rowLabelFor = useCallback(
    (doc: ReleasedDocument) => {
      const releasedOn = formatTimestamp(doc.released_at);
      const base = releasedOn
        ? `${doc.filename}, released ${releasedOn}`
        : doc.filename;
      if (!sameDayFilenames.has(`${doc.filename}|${releasedOn}`)) return base;
      const releasedAtTime = formatTimeOfDay(doc.released_at);
      return releasedAtTime ? `${base} at ${releasedAtTime}` : base;
    },
    [sameDayFilenames]
  );

  const columns = useMemo<ColumnDef<ReleasedDocument, unknown>[]>(() => {
    const selectColumn: ColumnDef<ReleasedDocument, unknown> = {
      id: 'select',
      header: ({ table }) => {
        // Select-all spans the rows currently passing the filters, and only
        // the selectable ones — a withdrawn row must never end up selected
        // via the header when its own checkbox refuses.
        const rows = table.getFilteredRowModel().rows.filter(isSelectable);
        const allSelected =
          rows.length > 0 && rows.every(row => row.getIsSelected());
        return (
          <Checkbox
            aria-label="Select all documents"
            checked={allSelected}
            onCheckedChange={() =>
              rows.forEach(row => row.toggleSelected(!allSelected))
            }
          />
        );
      },
      cell: ({ row }) => {
        const doc = row.original;
        const withdrawn = doc.status === 'withdrawn';
        const rowLabel = rowLabelFor(doc);
        return (
          <Checkbox
            aria-label={
              withdrawn
                ? // rowLabel, not a bare filename: a document released twice
                  // and then withdrawn yields two withdrawn rows sharing a
                  // name, and this branch would otherwise skip the
                  // disambiguation the selectable rows get.
                  `${rowLabel}, withdrawn — cannot be downloaded`
                : `Select ${rowLabel}`
            }
            checked={row.getIsSelected()}
            disabled={withdrawn}
            onCheckedChange={() => row.toggleSelected()}
          />
        );
      },
      enableSorting: false,
      enableHiding: false,
      size: 44,
      meta: { exportable: false, overflow: 'nowrap' },
    };

    const filenameColumn: ColumnDef<ReleasedDocument, unknown> = {
      id: 'filename',
      header: COLUMN_LABELS.filename,
      accessorFn: row => row.filename,
      enableSorting: true,
      meta: { overflow: 'truncate', minWidth: 220 },
      cell: ({ row }) => {
        const doc = row.original;
        const withdrawn = doc.status === 'withdrawn';
        return (
          <div className={rowTone(doc.status) ?? 'font-medium'}>
            {doc.filename}
            {withdrawn && (
              <span className="mt-1 block text-xs text-gray-500">
                {doc.withdrawn_at
                  ? `Withdrawn ${formatTimestamp(doc.withdrawn_at)}`
                  : 'Withdrawn'}
              </span>
            )}
          </div>
        );
      },
    };

    // Site label, most identifying part first: INV, name, PI. Any of the three
    // can be missing; none present means study-wide.
    const siteParts = (doc: ReleasedDocument) =>
      [doc.site_code, doc.site_name, doc.pi_name].filter(
        (part): part is string => Boolean(part)
      );

    const siteColumn: ColumnDef<ReleasedDocument, unknown> = {
      id: 'site',
      header: COLUMN_LABELS.site,
      accessorFn: row => siteParts(row).join(' · '),
      enableSorting: true,
      meta: {
        overflow: 'truncate',
        minWidth: 160,
        csvFormatter: (value, row) => {
          const parts = siteParts(row);
          if (parts.length > 0) return parts.join(' · ');
          return row.site_scope === 'site' ? 'Site' : 'Study';
        },
      },
      cell: ({ row }) => {
        const parts = siteParts(row.original);
        if (parts.length === 0) {
          // Only a genuinely study-wide release reads "Study". A
          // site-targeted release that yielded no label renders a placeholder
          // instead — saying "Study" there would misreport a site-restricted
          // document as study-wide. `site_scope` is absent on older builds,
          // where three blanks did mean study-wide.
          return row.original.site_scope === 'site' ? (
            <span className="text-gray-500">Site</span>
          ) : (
            <>Study</>
          );
        }
        return (
          <>
            <span className="block">{parts[0]}</span>
            {parts.length > 1 && (
              <span className="block text-xs text-gray-500">
                {parts.slice(1).join(' · ')}
              </span>
            )}
          </>
        );
      },
    };

    /*
     * OXX-1133. What this release was filed under — a study-level submission
     * id, or "Other". Never the operator's note behind "Other": that stays
     * Univo-side (Tori Haggett, 9 Sep 2026).
     */
    const associatedSubmissionColumn: ColumnDef<ReleasedDocument, unknown> = {
      id: 'associated_submission',
      header: COLUMN_LABELS.associated_submission,
      accessorFn: row => row.associated_submission,
      enableSorting: true,
      meta: {
        overflow: 'nowrap',
        minWidth: 160,
        csvFormatter: value => (value as string | null) ?? '',
      },
      cell: ({ getValue, row }) => (
        <span className={rowTone(row.original.status)}>
          {(getValue() as string | null) ?? '—'}
        </span>
      ),
    };

    /*
     * Spelled-out month rather than the terse table format: this is the date
     * the customer received a regulated document, and 7/30/26 reads as 30 July
     * to half the world.
     */
    const releasedColumn: ColumnDef<ReleasedDocument, unknown> = {
      id: 'released_at',
      header: COLUMN_LABELS.released_at,
      accessorFn: row => row.released_at,
      enableSorting: true,
      // `nullableDateSort` and `dateRange` are registered by DataTable itself,
      // so they aren't in TanStack's built-in name unions — hence the casts.
      sortingFn: 'nullableDateSort' as unknown as ColumnDef<
        ReleasedDocument,
        unknown
      >['sortingFn'],
      filterFn: 'dateRange' as unknown as ColumnDef<
        ReleasedDocument,
        unknown
      >['filterFn'],
      meta: {
        overflow: 'nowrap',
        minWidth: 140,
        csvFormatter: value => formatTimestamp(value as string | null) ?? '',
      },
      cell: ({ getValue, row }) => (
        <span className={rowTone(row.original.status)}>
          {formatTimestamp(getValue() as string | null) ?? '—'}
        </span>
      ),
    };

    return [
      selectColumn,
      filenameColumn,
      ...(showSite ? [siteColumn] : []),
      associatedSubmissionColumn,
      releasedColumn,
    ];
  }, [showSite, isSelectable, rowLabelFor]);

  const managedColumns: ManagedColumn[] = useMemo(
    () =>
      manageableColumns.map(column => ({
        id: column.id,
        label: COLUMN_LABELS[column.id] ?? column.id,
      })),
    [manageableColumns]
  );

  const columnFilters = useMemo(
    () =>
      releasedRange.from || releasedRange.to
        ? [{ id: 'released_at', value: releasedRange }]
        : [],
    [releasedRange]
  );

  // What a CSV export would actually write. DataTable reports the post-filter
  // count, but only while it is mounted — TableStateGate unmounts it when
  // there are no documents at all, while the toolbar (Export included) renders
  // outside the gate. Falling back to 0 there stops a stale count from a
  // previous render leaving Export enabled on an empty table, where it would
  // write a headers-only CSV.
  const exportRowCount = documents.length === 0 ? 0 : tableRowCount;

  const handleReleasedRangeChange = (value: DateRangeFilterValue) => {
    setReleasedRange(value);
    // A narrower result set can leave the current page past the end.
    setPagination(previous => ({ ...previous, pageIndex: 0 }));
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 p-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            {selectedIds.length > 0
              ? `${selectedIds.length} of ${selectableIds.length} selected`
              : `${documents.length} document${documents.length === 1 ? '' : 's'}`}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangeFilter
            label="Released"
            value={releasedRange}
            onChange={handleReleasedRangeChange}
          />
          <ColumnManagerPopover
            columns={managedColumns}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            columnOrder={columnOrder}
            onColumnOrderChange={setColumnOrder}
            columnPinning={columnPinning}
            onColumnPinningChange={setColumnPinning}
            onReset={resetToDefaults}
          />
          <ExportCsvButton
            dataTableRef={dataTableRef}
            filename="final_documents.csv"
            rowCount={exportRowCount}
          />
          <Button
            type="button"
            variant="primary"
            disabled={selectedIds.length === 0 || isDownloading}
            onClick={() => onDownload(selectedIds)}
            className="h-auto w-auto gap-2 px-4 py-2 text-sm font-medium"
          >
            <Download className="h-4 w-4" />
            <span>{isDownloading ? 'Preparing…' : 'Download selected'}</span>
          </Button>
        </div>
      </div>

      {zipExportId && (
        <ZipExportNotice
          key={zipExportId}
          exportId={zipExportId}
          className="rounded-none border-0 border-b border-gray-200"
        />
      )}

      {downloadError && (
        <Alert
          variant="destructive"
          className="rounded-none border-0 border-b border-gray-200"
        >
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{downloadError}</AlertDescription>
        </Alert>
      )}

      <TableStateGate
        isLoading={isLoading}
        error={error}
        isEmpty={documents.length === 0}
        errorMessage="We couldn’t load these documents. Please try again."
        emptyMessage="No final documents yet. Documents Univo has released to you will appear here."
      >
        <DataTable
          ref={dataTableRef}
          columns={columns}
          data={documents}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          columnOrder={['select', ...columnOrder]}
          onColumnOrderChange={setColumnOrder}
          // `select` leads the order, so it must also lead the left-pinned
          // set. A pinned column's sticky offset counts only the pinned
          // columns before it, so leaving `select` unpinned would stick
          // Document at left: 0 and slide it over the checkboxes as soon as
          // the table scrolls horizontally.
          columnPinning={{
            ...columnPinning,
            left: ['select', ...(columnPinning.left ?? [])],
          }}
          onColumnPinningChange={setColumnPinning}
          columnFilters={columnFilters}
          // Derived wholly from `releasedRange`, the one filter control this
          // table has. Nothing inside the table sets a filter, so there is no
          // change to propagate back — the DateRangeFilter owns the state.
          onColumnFiltersChange={() => undefined}
          pagination={{
            pageIndex: pagination.pageIndex,
            pageSize: pagination.pageSize,
            onPaginationChange: setPagination,
          }}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          enableRowSelection={isSelectable}
          onExportRowCountChange={setTableRowCount}
          emptyMessage="No documents match these filters."
        />
      </TableStateGate>
    </div>
  );
};

export default ReleasedDocumentsTable;
